import React, { useState } from 'react';
import { TouchableOpacity } from 'components/utility/Buttons';
import { SizableText, XStack, ScrollView, Stack, useTheme } from 'tamagui';
import { Platform } from 'react-native';
import Panda from 'components/utility/NormalPanda';
import { profileSave } from 'reducers/slices/profile';
import { openSideBar } from 'reducers/slices/sidebar';
import { toggleLogin } from 'reducers/slices/loginstatus';
import { IMenu } from 'interfaces/component/Menu';
import AwesomeAlert from 'react-native-awesome-alerts';
import { useRouter } from 'solito/router';
import { auth } from 'utils/Firebase';
import { useDispatch, useSelector } from 'react-redux';
import { deleteUser } from 'firebase/auth';
import { useDeleteUserMutation } from 'utils/RTKGraphQL';
import {
  NewspaperVariantOutlineIcon,
  BookSearchOutlineIcon,
  BookOpenOutlineIcon,
  CogsIcon,
  FormatListNumberedIcon,
  BookmarkOutlineIcon,
  MailOutlineIcon,
  PaymentIcon,
  CommaCircleOutlineIcon,
  BallotOutlineIcon,
  ShieldAlertOutlineIcon,
  GraphIcon,
  SchoolIcon,
  HearingIcon,
  LogoutIcon,
  LoginIcon,
  UserIcon,
} from 'app/components/utility/SVGIcons';
import { LinkButton } from 'components/utility/Buttons';

const Menu = (props: IMenu) => {
  const theme = useTheme();
  const { push } = useRouter();
  const [showAlert, setShowAlert] = useState(false);
  const profile = useSelector((state) => state.profile);
  const dispatch = useDispatch();
  const [deleteUserMutation] = useDeleteUserMutation();

  const launchAlert = () => {
    setShowAlert(true);
  };

  const hideAlert = () => {
    setShowAlert(false);
  };

  const deleteAccount = async () => {
    try {
      await deleteUserMutation({ username: profile.username }).unwrap();

      const user = auth.currentUser;

      if (user) {
        await deleteUser(user);
        dispatch(toggleLogin(false));
        push('/');
      }

      dispatch(profileSave(null));
      dispatch(openSideBar(false));
      push('/');
    } catch (error) {
      console.error('Failed to delete account:', error);
    }
  };

  const logout = () => {
    dispatch(profileSave(null));
    auth.signOut();
    dispatch(openSideBar(false));
    push('/');
  };

  const login = () => {
    dispatch(toggleLogin(true));
  };

  const goHome = () => {
    dispatch(openSideBar(false));
    push('/');
  };

  return (
    <ScrollView
      scrollsToTop={false}
      flexGrow={1}
      flexShrink={0}
      bg={theme.sidebarGray.get()}
      nativeID="menu"
      style={{
        overflowY: 'scroll',
      }}
    >
      <Stack height="100%">
        <TouchableOpacity onPress={goHome}>
          <Stack mt={50} mb={40}>
            <Panda />
          </Stack>
        </TouchableOpacity>
        {Platform.OS === 'web' && (
          <>
            <LinkButton href="/">
              <XStack gap={20}>
                <NewspaperVariantOutlineIcon
                  size={20}
                  color={theme.mainColor.get()}
                />
                <SizableText
                  color={theme.mainColor.get()}
                  fontSize="$sm"
                  fontWeight="bold"
                >
                  Articles
                </SizableText>
              </XStack>
            </LinkButton>
            <LinkButton href="/quizzes/">
              <XStack gap={20}>
                <BookOpenOutlineIcon size={20} color={theme.mainColor.get()} />
                <SizableText
                  color={theme.mainColor.get()}
                  fontSize="$sm"
                  fontWeight="bold"
                >
                  Quizzes
                </SizableText>
              </XStack>
            </LinkButton>
            <LinkButton href="/dictionary/">
              <XStack gap={20}>
                <BookSearchOutlineIcon
                  size={20}
                  color={theme.mainColor.get()}
                />
                <SizableText
                  color={theme.mainColor.get()}
                  fontSize="$sm"
                  fontWeight="bold"
                >
                  Dictionary
                </SizableText>
              </XStack>
            </LinkButton>
            <LinkButton href="/settings/">
              <XStack gap={20}>
                <CogsIcon size={20} color={theme.mainColor.get()} />
                <SizableText
                  color={theme.mainColor.get()}
                  fontSize="$sm"
                  fontWeight="bold"
                >
                  Settings
                </SizableText>
              </XStack>
            </LinkButton>
          </>
        )}
        <LinkButton href="/wordlist/">
          <XStack gap={20}>
            <FormatListNumberedIcon size={20} color={theme.mainColor.get()} />
            <SizableText
              color={theme.mainColor.get()}
              fontSize="$sm"
              fontWeight="bold"
            >
              Word List
            </SizableText>
          </XStack>
        </LinkButton>
        <LinkButton href="/sentencelist/">
          <XStack gap={20}>
            <FormatListNumberedIcon size={20} color={theme.mainColor.get()} />
            <SizableText
              color={theme.mainColor.get()}
              fontSize="$sm"
              fontWeight="bold"
            >
              Sentence List
            </SizableText>
          </XStack>
        </LinkButton>
        <LinkButton href="/bookmarks/">
          <XStack gap={20}>
            <BookmarkOutlineIcon size={20} color={theme.mainColor.get()} />
            <SizableText
              color={theme.mainColor.get()}
              fontSize="$sm"
              fontWeight="bold"
            >
              Bookmarks
            </SizableText>
          </XStack>
        </LinkButton>
        <LinkButton href="/contact/">
          <XStack gap={20}>
            <MailOutlineIcon size={22} color={theme.mainColor.get()} />
            <SizableText
              color={theme.mainColor.get()}
              fontSize="$sm"
              fontWeight="bold"
            >
              Contact
            </SizableText>
          </XStack>
        </LinkButton>
        {profile && (
          <LinkButton href="/statistics/">
            <XStack gap={20}>
              <GraphIcon size={20} color={theme.mainColor.get()} />
              <SizableText
                color={theme.mainColor.get()}
                fontSize="$sm"
                fontWeight="bold"
              >
                Statistics
              </SizableText>
            </XStack>
          </LinkButton>
        )}
        <LinkButton href="/terms/">
          <XStack gap={20}>
            <BallotOutlineIcon size={20} color={theme.mainColor.get()} />
            <SizableText
              color={theme.mainColor.get()}
              fontSize="$sm"
              fontWeight="bold"
            >
              Terms of Use
            </SizableText>
          </XStack>
        </LinkButton>
        <LinkButton href="/privacy/">
          <XStack gap={20}>
            <ShieldAlertOutlineIcon size={20} color={theme.mainColor.get()} />
            <SizableText
              color={theme.mainColor.get()}
              fontSize="$sm"
              fontWeight="bold"
            >
              Privacy Policy
            </SizableText>
          </XStack>
        </LinkButton>
        <LinkButton href="/hsk-charts/">
          <XStack gap={20}>
            <SchoolIcon size={20} color={theme.mainColor.get()} />
            <SizableText
              color={theme.mainColor.get()}
              fontSize="$sm"
              fontWeight="bold"
            >
              HSK Charts
            </SizableText>
          </XStack>
        </LinkButton>
        <LinkButton href="/pinyin-chart/">
          <XStack gap={20}>
            <HearingIcon size={20} color={theme.mainColor.get()} />
            <SizableText
              color={theme.mainColor.get()}
              fontSize="$sm"
              fontWeight="bold"
            >
              Pinyin Chart
            </SizableText>
          </XStack>
        </LinkButton>
        <LinkButton href="/profile/">
          <XStack gap={20}>
            <UserIcon size={20} color={theme.mainColor.get()} />
            <SizableText
              color={theme.mainColor.get()}
              fontSize="$sm"
              fontWeight="bold"
            >
              Profile
            </SizableText>
          </XStack>
        </LinkButton>
        {profile ? (
          <Stack
            pl={20}
            py={15}
            hoverStyle={{ bg: '$gray300' }}
            pressStyle={{ opacity: 0.5 }}
            onPress={logout}
          >
            <XStack gap={20}>
              <LogoutIcon size={20} color={theme.mainColor.get()} />
              <SizableText
                color={theme.mainColor.get()}
                fontSize="$sm"
                fontWeight="bold"
              >
                Logout
              </SizableText>
            </XStack>
          </Stack>
        ) : (
          <Stack
            pl={20}
            py={15}
            hoverStyle={{ bg: '$gray300' }}
            pressStyle={{ opacity: 0.5 }}
            onPress={login}
          >
            <XStack gap={20}>
              <LoginIcon size={20} color={theme.mainColor.get()} />
              <SizableText
                color={theme.mainColor.get()}
                fontSize="$sm"
                fontWeight="bold"
              >
                Login
              </SizableText>
            </XStack>
          </Stack>
        )}
        <AwesomeAlert
          show={showAlert}
          showProgress={false}
          title="Delete Account"
          message="Note: This action is permanent and cannot be undone."
          closeOnTouchOutside={true}
          closeOnHardwareBackPress={false}
          showCancelButton={true}
          showConfirmButton={true}
          cancelText="No, cancel"
          confirmText="Yes, delete it"
          confirmButtonColor="#DD6B55"
          onCancelPressed={() => {
            hideAlert();
          }}
          onConfirmPressed={async () => {
            hideAlert();
            deleteAccount();
          }}
        />
      </Stack>
    </ScrollView>
  );
};

export default Menu;
